import * as React from "react";
import { cn } from "../lib/utils";
import { CopyButton } from "./code-block";

export interface KeyValueItem {
  key: React.ReactNode;
  value: React.ReactNode;
  /** Render the value in font-mono (identifiers, URLs, keys). */
  mono?: boolean;
  /** Show a copy button; copies copyValue when provided, else the stringified value. */
  copyable?: boolean;
  copyValue?: string;
}

export interface KeyValueListProps extends React.HTMLAttributes<HTMLDListElement> {
  items: KeyValueItem[];
  /** Columns for the grid layout (1 = stacked). Default 1. */
  columns?: 1 | 2 | 3;
}

const KeyValueList = React.forwardRef<HTMLDListElement, KeyValueListProps>(
  ({ className, items, columns = 1, ...props }, ref) => (
    <dl
      ref={ref}
      className={cn(
        "grid gap-x-6 gap-y-3",
        columns === 1 && "grid-cols-1",
        columns === 2 && "sm:grid-cols-2",
        columns === 3 && "sm:grid-cols-2 lg:grid-cols-3",
        className,
      )}
      {...props}
    >
      {items.map((item, i) => (
        <div key={i} className="flex flex-col gap-0.5">
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {item.key}
          </dt>
          <dd className="flex items-center gap-2 text-sm text-foreground">
            <span
              className={cn(
                "break-all",
                item.mono && "font-mono text-[13px]",
              )}
            >
              {item.value}
            </span>
            {item.copyable ? (
              <CopyButton
                text={item.copyValue ?? String(item.value)}
                className="shrink-0"
              />
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  ),
);
KeyValueList.displayName = "KeyValueList";

export { KeyValueList };
